/**
 * 音频校验工具类
 */

// 音频校验错误类型
export const AUDIO_VALIDATE_ERROR_TYPE = {
  // 不允许的文件类型
  INVALID_TYPE: "INVALID_TYPE",
  // 文件大小小于最小限制
  INVALID_MIN_FILE_SIZE: "INVALID_MIN_FILE_SIZE",
  // 文件大小超过最大限制
  INVALID_MAX_FILE_SIZE: "INVALID_MAX_FILE_SIZE",
  // 时长小于最小限制
  INVALID_MIN_DURATION: "INVALID_MIN_DURATION",
  // 时长超过最大限制
  INVALID_MAX_DURATION: "INVALID_MAX_DURATION",
  // 音频元数据加载失败（文件损坏、解码失败等）
  LOAD_ERROR: "LOAD_ERROR",
} as const;

export type AudioValidateErrorType =
  (typeof AUDIO_VALIDATE_ERROR_TYPE)[keyof typeof AUDIO_VALIDATE_ERROR_TYPE];

export interface AudioValidationOptions {
  minDuration?: number; // 最小时长（秒）
  maxDuration?: number; // 最大时长（秒）
  maxFileSize?: number; // 最大文件大小（字节）
  minFileSize?: number; // 最小文件大小（字节）
  allowedTypes?: string[]; // 允许的文件类型（MIME类型）
}

export interface AudioMetadata {
  duration: number;
  fileSize: number;
  fileType: string;
}

/**
 * 获取音频元数据
 * @param file 音频文件
 * @returns Promise<AudioMetadata>
 */
export function getAudioMetadata(file: File): Promise<AudioMetadata> {
  return new Promise((resolve, reject) => {
    const audio = document.createElement("audio");
    const url = URL.createObjectURL(file);

    audio.onloadedmetadata = () => {
      const metadata: AudioMetadata = {
        duration: audio.duration,
        fileSize: file.size,
        fileType: file.type,
      };

      URL.revokeObjectURL(url);
      resolve(metadata);
    };

    audio.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load audio metadata"));
    };

    audio.src = url;
  });
}

/**
 * 校验音频时长
 * @returns 错误类型，校验通过则返回 null
 */
export function validateAudioDuration(
  duration: number,
  minDuration?: number,
  maxDuration?: number
): AudioValidateErrorType | null {
  if (minDuration !== undefined && duration < minDuration) {
    return AUDIO_VALIDATE_ERROR_TYPE.INVALID_MIN_DURATION;
  }

  if (maxDuration !== undefined && duration > maxDuration) {
    return AUDIO_VALIDATE_ERROR_TYPE.INVALID_MAX_DURATION;
  }

  return null;
}

/**
 * 校验文件大小
 * @returns 错误类型，校验通过则返回 null
 */
export function validateFileSize(
  fileSize: number,
  minSize?: number,
  maxSize?: number
): AudioValidateErrorType | null {
  if (minSize !== undefined && fileSize < minSize) {
    return AUDIO_VALIDATE_ERROR_TYPE.INVALID_MIN_FILE_SIZE;
  }

  if (maxSize !== undefined && fileSize > maxSize) {
    return AUDIO_VALIDATE_ERROR_TYPE.INVALID_MAX_FILE_SIZE;
  }

  return null;
}

/**
 * 校验文件类型
 * @param fileType 文件MIME类型
 * @param allowedTypes 允许的文件类型数组 为空则都允许
 * @returns 校验结果
 */
export function validateFileType(
  fileType: string,
  allowedTypes: string[] = []
): boolean {
  return allowedTypes.length === 0 || allowedTypes.includes(fileType);
}

/**
 * 综合校验音频文件
 * @param file 音频文件
 * @param options 校验选项
 * @returns Promise<AudioValidateErrorType | null> 校验通过返回 null，否则返回具体的错误类型
 */
export async function validateAudio(
  file: File,
  options: AudioValidationOptions = {}
): Promise<AudioValidateErrorType | null> {
  if (options.allowedTypes && options.allowedTypes.length > 0) {
    if (!validateFileType(file.type, options.allowedTypes)) {
      return AUDIO_VALIDATE_ERROR_TYPE.INVALID_TYPE;
    }
  }

  if (options.minFileSize !== undefined || options.maxFileSize !== undefined) {
    const sizeError = validateFileSize(
      file.size,
      options.minFileSize,
      options.maxFileSize
    );
    if (sizeError) {
      return sizeError;
    }
  }

  if (options.minDuration === undefined && options.maxDuration === undefined) {
    return null;
  }

  let metadata: AudioMetadata;
  try {
    metadata = await getAudioMetadata(file);
  } catch (error) {
    return AUDIO_VALIDATE_ERROR_TYPE.LOAD_ERROR;
  }

  return validateAudioDuration(
    metadata.duration,
    options.minDuration,
    options.maxDuration
  );
}
